import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { categoriesApi } from 'api/categoriesApi';
import { valuePackApi } from 'api/valuePackApi';
import { DEFAULT_FORM_VALUES } from 'config/valuePackFormConfig';

// NEW OPTIMIZED THUNK - Single call for valuepacks + categories + features
export const fetchValuePacksOptimized = createAsyncThunk(
  'valuePack/fetchValuePacksOptimized',
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await valuePackApi.fetchAllValuePacksOptimized(options);
      return response;
    } catch (error) {
      console.error('Error in fetchValuePacksOptimized thunk:', error);
      return rejectWithValue(error.message || 'Failed to fetch value packs optimized');
    }
  }
);

// NEW OPTIMIZED THUNK - Categories with features
export const fetchCategoriesOptimized = createAsyncThunk(
  'valuePack/fetchCategoriesOptimized',
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.fetchAllActiveCategoriesOptimized();
      return response;
    } catch (error) {
      console.error('Error in fetchCategoriesOptimized thunk:', error);
      return rejectWithValue(error.message || 'Failed to fetch categories optimized');
    }
  }
);

export const fetchValuePackTiers = createAsyncThunk(
  'valuePack/fetchValuePackTiers',
  async (id, { rejectWithValue }) => {
    try {
      const tiers = await valuePackApi.getValuePackTiers(id);
      return { id, tiers };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch value pack tiers');
    }
  }
);

export const saveValuePackTiers = createAsyncThunk(
  'valuePack/saveValuePackTiers',
  async ({ id, tiersData }, { rejectWithValue }) => {
    try {
      const savedTiers = await valuePackApi.saveValuePackTiers(id, tiersData);
      return { id, tiers: savedTiers };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save value pack tiers');
    }
  }
);

export const deleteValuePackTier = createAsyncThunk(
  'valuePack/deleteValuePackTier',
  async ({ valuePackId, tierId }, { rejectWithValue }) => {
    try {
      await valuePackApi.deleteValuePackTier(valuePackId, tierId);
      return { valuePackId, tierId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete value pack tier');
    }
  }
);

export const deleteValuePackTiersByCriteria = createAsyncThunk(
  'valuePack/deleteValuePackTiersByCriteria',
  async ({ valuePackId, duration, countryCode }, { rejectWithValue }) => {
    try {
      await valuePackApi.deleteValuePackTiersByCriteria(valuePackId, { duration, countryCode });
      return { valuePackId, duration, countryCode };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete value pack tiers by criteria');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'valuePack/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const categories = await categoriesApi.fetchAllActiveCategories();      
      return Array.isArray(categories) ? categories : [];
    } catch (error) {
      console.error('Error in fetchCategories thunk:', error);
      return rejectWithValue(error.message || 'Failed to fetch categories');
    }
  }
);

// Lean paginated list for the admin table (see valuePackApi.fetchValuePackList)
export const fetchValuePackList = createAsyncThunk(
  'valuePack/fetchValuePackList',
  async (options = {}, { rejectWithValue }) => {
    try {
      return await valuePackApi.fetchValuePackList(options);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch value packs');
    }
  }
);

// Complete data of ONE value pack, fetched on demand by view/edit/tier screens
export const fetchValuePackDetails = createAsyncThunk(
  'valuePack/fetchValuePackDetails',
  async (id, { rejectWithValue }) => {
    try {
      return await valuePackApi.fetchValuePackDetails(id);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch value pack');
    }
  }
);

export const fetchValuePackById = createAsyncThunk(
  'valuePack/fetchValuePackById',
  async (id, { rejectWithValue }) => {
    try {
      const valuePack = await valuePackApi.fetchValuePackById(id);
      return valuePack;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch value pack');
    }
  }
);

export const createValuePack = createAsyncThunk(
  'valuePack/createValuePack',
  async (valuePackData, { rejectWithValue }) => {
    try {
      const formDataWithFiles = {
        ...valuePackData,
        imageFile: valuePackData.imageFile || null,
        bannerImageFile: valuePackData.bannerImageFile || null
      };

      const newValuePack = await valuePackApi.createValuePack(formDataWithFiles);
      return newValuePack;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create value pack');
    }
  }
);

export const updateValuePack = createAsyncThunk(
  'valuePack/updateValuePack',
  async ({ id, valuePackData }, { rejectWithValue }) => {
    try {
      const formDataWithFiles = {
        ...valuePackData,
        imageFile: valuePackData.imageFile || null,
        bannerImageFile: valuePackData.bannerImageFile || null
      };

      const updatedValuePack = await valuePackApi.updateValuePack(id, formDataWithFiles);
      return updatedValuePack;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update value pack');
    }
  }
);

export const deleteValuePack = createAsyncThunk(
  'valuePack/deleteValuePack',
  async ({ id, label } = {}, { rejectWithValue }) => {
    try {
      await valuePackApi.deleteValuePack(id, label);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete value pack');
    }
  }
);

export const updateValuePackTiers = createAsyncThunk(
  'valuePack/updateValuePackTiers',
  async ({ id, tiers }, { rejectWithValue }) => {
    try {
      await valuePackApi.updateValuePackTiers(id, tiers);
      return { id, tiers };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update tiers');
    }
  }
);

export const fetchCommonValuePackData = createAsyncThunk(
  'valuePack/fetchCommonValuePackData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await valuePackApi.fetchCommonData();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch common data');
    }
  }
);

export const updateCommonValuePackData = createAsyncThunk(
  'valuePack/updateCommonValuePackData',
  async (commonData, { rejectWithValue }) => {
    try {
      const response = await valuePackApi.updateCommonData(commonData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update common data');
    }
  }
);

const initialState = {
  valuePacks: [],
  currentValuePack: null,
  commonData: null,

  allCategoriesWithFeatures: [], // All categories with their features for selection
  
  // Pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  
  // Form state
  formData: { ...DEFAULT_FORM_VALUES },
  currentStep: 0,
  formErrors: {},
  isEditMode: false,
  
  // Global loading states
  loading: false,
  submitting: false,
  
  // Error handling
  error: null,
  
  currentOperation: null
};

const valuePackSlice = createSlice({
  name: 'valuePack',
  initialState,
  reducers: {
    setCommonData: (state, action) => {
      state.commonData = action.payload;
    },
    setFormData: (state, action) => {
      state.formData = { 
        ...state.formData, 
        ...action.payload 
      };
    },
    
    resetFormData: (state) => {
      state.formData = { ...DEFAULT_FORM_VALUES };
    },
    
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
    
    setCurrentStep: (state, action) => {
      const newStep = action.payload;
      if (newStep >= 0 && newStep <= 4) {
        state.currentStep = newStep;
      }
    },
    
    nextStep: (state) => {
      if (state.currentStep < 4) {
        state.currentStep += 1;
      }
    },
    
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },
    
    setFormErrors: (state, action) => {
      state.formErrors = action.payload || {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    setEditMode: (state, action) => {
      state.isEditMode = Boolean(action.payload);
    },

    setCurrentValuePack: (state, action) => {
      const payload = action.payload;
      state.currentValuePack = {
        ...payload,
        categories: Array.isArray(payload.categories) 
          ? payload.categories.map(cat => cat.id)
          : []
      };

      state.formData = {
        ...DEFAULT_FORM_VALUES,
        ...state.currentValuePack
      };
    },

    clearFeatures: (state) => {
      state.featuresByCategory = {};
      state.allCategoriesWithFeatures = [];
    },
    
    resetForm: (state) => {
      state.formData = { ...DEFAULT_FORM_VALUES };
      state.currentStep = 0;
      state.formErrors = {};
      state.isEditMode = false;
      state.error = null;
      state.currentValuePack = null;
    },
    
    addTag: (state, action) => {
      const tag = action.payload;
      if (!state.formData.tags.includes(tag)) {
        state.formData.tags.push(tag);
      }
    },
    
    removeTag: (state, action) => {
      const tagIndex = action.payload;
      if (tagIndex >= 0 && tagIndex < state.formData.tags.length) {
        state.formData.tags.splice(tagIndex, 1);
      }
    },

    updateValuePackTiersLocal: (state, action) => {
      const { valuePackId, tiers } = action.payload;
      
      const index = state.valuePacks.findIndex(pack => pack.id === valuePackId);
      if (index !== -1) {
        state.valuePacks[index].tiers = tiers;
        state.valuePacks[index].updatedAt = new Date().toISOString();
      }
      
      if (state.currentValuePack && state.currentValuePack.id === valuePackId) {
        state.currentValuePack.tiers = tiers;
        state.currentValuePack.updatedAt = new Date().toISOString();
      }
      
      if (state.isEditMode && state.formData) {
        state.formData.tiers = tiers;
      }
    },

    // Set features for multiple categories at once
    setFeaturesForCategories: (state, action) => {
      const featuresData = action.payload;
      Object.keys(featuresData).forEach(categoryId => {
        state.featuresByCategory[categoryId] = featuresData[categoryId];
      });
    },

    setFeaturesForCategory: (state, action) => {
      const { categoryId, features, additionalFeatures } = action.payload;
      if (!state.featuresByCategory[categoryId]) {
        state.featuresByCategory[categoryId] = {};
      }
      if (features) {
        state.featuresByCategory[categoryId].features = features;
      }
      if (additionalFeatures) {
        state.featuresByCategory[categoryId].additionalFeatures = additionalFeatures;
      }
    },

    // Set all categories with features for selection
    setAllCategoriesWithFeatures: (state, action) => {
      state.allCategoriesWithFeatures = Array.isArray(action.payload) ? action.payload : [];
    },

    // Pagination actions
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },

    setTiers: (state, action) => {
      const { tiers } = action.payload;
      state.formData.tiers = tiers;
      
      if (state.currentValuePack) {
        state.currentValuePack.tiers = tiers;
      }
      
      // Update the specific value pack in the list
      const index = state.valuePacks.findIndex(pack => pack.id === state.currentValuePack?.id);
      if (index !== -1) {
        state.valuePacks[index].tiers = tiers;
      }
    },

    // Add tier to form data (for immediate UI update)
    addTierToForm: (state, action) => {
      const tier = action.payload;
      if (!state.formData.tiers) {
        state.formData.tiers = [];
      }
      state.formData.tiers.push(tier);
    },
    
    updateTierInForm: (state, action) => {
      const { tierId, updates } = action.payload;
      if (state.formData.tiers) {
        const index = state.formData.tiers.findIndex(t => t.id === tierId);
        if (index !== -1) {
          state.formData.tiers[index] = { ...state.formData.tiers[index], ...updates };
        }
      }
    },

    removeTierFromForm: (state, action) => {
      const tierId = action.payload;
      if (state.formData.tiers) {
        state.formData.tiers = state.formData.tiers.filter(t => t.id !== tierId);
      }
    }
  },
  
  extraReducers: (builder) => {
    builder
      // NEW - Optimized ValuePacks Loading
      .addCase(fetchValuePacksOptimized.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchValuePacksOptimized';
      })
      .addCase(fetchValuePacksOptimized.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOperation = null;
        
        const { valuePacks, allCategories, pagination } = action.payload;
        
        // Set value packs with enriched categories
        state.valuePacks = Array.isArray(valuePacks) ? valuePacks : [];
        
        // Store all categories with features for selection
        if (Array.isArray(allCategories)) {
          state.allCategoriesWithFeatures = allCategories;
          
          // Extract features from categories and store in featuresByCategory
          const featuresData = {};
          allCategories.forEach(category => {
            if (category && category.id) {
              featuresData[category.id] = {
                features: Array.isArray(category.features) ? category.features : [],
                additionalFeatures: Array.isArray(category.additionalFeatures) ? category.additionalFeatures : []
              };
            }
          });
          state.featuresByCategory = { ...state.featuresByCategory, ...featuresData };
        }
        
        // Set pagination (backend nests it as { valuePacks: {...} })
        if (pagination) {
          state.pagination = pagination.valuePacks || pagination;
        }
      })
      .addCase(fetchValuePacksOptimized.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch value packs optimized';
        state.currentOperation = null;
      })

      // Lean list — replaces only the rows + pagination. allCategoriesWithFeatures
      // is deliberately left alone; it is loaded on demand by the screens that need it.
      .addCase(fetchValuePackList.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchValuePackList';
      })
      .addCase(fetchValuePackList.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOperation = null;
        const { valuePacks, pagination } = action.payload;
        state.valuePacks = Array.isArray(valuePacks) ? valuePacks : [];
        if (pagination) {
          state.pagination = pagination;
        }
      })
      .addCase(fetchValuePackList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch value packs';
        state.currentOperation = null;
      })

      // One value pack, complete (view / edit / tiers)
      .addCase(fetchValuePackDetails.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchValuePackDetails';
        // Drop a previously opened pack so a screen never shows stale data
        // for a different id while the new one loads.
        if (state.currentValuePack && state.currentValuePack.id !== action.meta.arg) {
          state.currentValuePack = null;
        }
      })
      .addCase(fetchValuePackDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOperation = null;
        state.currentValuePack = action.payload;
      })
      .addCase(fetchValuePackDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch value pack';
        state.currentOperation = null;
      })

      // NEW - Optimized Categories Loading
      .addCase(fetchCategoriesOptimized.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchCategoriesOptimized';
      })
      .addCase(fetchCategoriesOptimized.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOperation = null;
        
        const { categories, featuresData } = action.payload;
        
        // Set categories with features for selection
        if (Array.isArray(categories)) {
          state.allCategoriesWithFeatures = categories;
        }
        
        // Set features data
        if (featuresData) {
          state.featuresByCategory = { ...state.featuresByCategory, ...featuresData };
        } else if (Array.isArray(categories)) {
          // Fallback: extract features from categories array
          const featuresDataFromCategories = {};
          categories.forEach(category => {
            if (category && category.id) {
              featuresDataFromCategories[category.id] = {
                features: Array.isArray(category.features) ? category.features : [],
                additionalFeatures: Array.isArray(category.additionalFeatures) ? category.additionalFeatures : []
              };
            }
          });
          state.featuresByCategory = { ...state.featuresByCategory, ...featuresDataFromCategories };
        }
      })
      .addCase(fetchCategoriesOptimized.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch categories optimized';
        state.allCategoriesWithFeatures = [];
        state.currentOperation = null;
      })

      // Keep existing cases for backward compatibility
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchCategories';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        // For backward compatibility, also populate allCategoriesWithFeatures
        state.allCategoriesWithFeatures = Array.isArray(action.payload) ? action.payload : [];
        state.currentOperation = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch categories';
        state.allCategoriesWithFeatures = [];
        state.currentOperation = null;
      })
      
      // Fetch Value Pack by ID
      .addCase(fetchValuePackById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchValuePackById';
      })
      .addCase(fetchValuePackById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentValuePack = action.payload;
        state.currentOperation = null;
        
        // Populate form data
        const payload = action.payload || {};
        state.formData = {
          name: payload.name || '',
          subTitle: payload.subTitle || '',
          description: payload.description || '',
          shortDescription: payload.shortDescription || '',
          slug: payload.slug || '',
          active: payload.active !== undefined ? payload.active : true,
          featured: payload.featured || false,
          imageUrl: payload.imageUrl || '',
          bannerImageUrl: payload.bannerImageUrl || '',
          categories: Array.isArray(payload.categories)
            ? payload.categories.map(cat => cat.id)
            : [],
          tags: Array.isArray(payload.tags) ? payload.tags : [],
          targetIndustries: Array.isArray(payload.targetIndustries)
            ? payload.targetIndustries 
            : ['all'],
          // The custom "industry" filter is the form's Target Industries field:
          // packs saved before it existed get it from targetIndustries
          filterValues: (() => {
            const values =
              payload.filterValues && typeof payload.filterValues === 'object'
                ? { ...payload.filterValues }
                : {};
            const industries = (payload.targetIndustries || []).filter(
              v => v && v !== 'all',
            );
            if (!values.industry?.length && industries.length) {
              values.industry = industries;
            }
            return values;
          })(),
          publishedAt: payload.publishedAt || null,
          expiresAt: payload.expiresAt || null,
          tiers: Array.isArray(payload.tiers) ? payload.tiers : []
        };
      })  
      .addCase(fetchValuePackById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Create Value Pack
      .addCase(createValuePack.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'createValuePack';
      })
      .addCase(createValuePack.fulfilled, (state, action) => {
        state.submitting = false;
        state.valuePacks.unshift(action.payload);
        state.currentValuePack = action.payload;
        state.currentOperation = null;
      })
      .addCase(createValuePack.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Update Value Pack
      .addCase(updateValuePack.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'updateValuePack';
      })
      .addCase(updateValuePack.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.valuePacks.findIndex(pack => pack.id === action.payload.id);
        if (index !== -1) {
          state.valuePacks[index] = action.payload;
        }
        state.currentValuePack = action.payload;
        state.currentOperation = null;
      })
      .addCase(updateValuePack.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Delete Value Pack
      .addCase(deleteValuePack.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'deleteValuePack';
      })
      .addCase(deleteValuePack.fulfilled, (state, action) => {
        state.submitting = false;
        state.valuePacks = state.valuePacks.filter(pack => pack.id !== action.payload);
        state.currentOperation = null;
      })
      .addCase(deleteValuePack.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Update Value Pack Tiers
      .addCase(updateValuePackTiers.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'updateValuePackTiers';
      })
      .addCase(updateValuePackTiers.fulfilled, (state, action) => {
        state.submitting = false;
        const { id, tiers } = action.payload;
        state.currentOperation = null;
        
        const index = state.valuePacks.findIndex(pack => pack.id === id);
        if (index !== -1) {
          state.valuePacks[index].tiers = tiers;
          state.valuePacks[index].updatedAt = new Date().toISOString();
        }
        
        if (state.currentValuePack && state.currentValuePack.id === id) {
          state.currentValuePack.tiers = tiers;
          state.currentValuePack.updatedAt = new Date().toISOString();
        }
        
        if (state.isEditMode && state.formData) {
          state.formData.tiers = tiers;
        }
      })
      .addCase(updateValuePackTiers.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })

      .addCase(fetchValuePackTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchValuePackTiers';
      })
      .addCase(fetchValuePackTiers.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOperation = null;
        
        const { id, tiers } = action.payload;
        
        // Update form data
        state.formData.tiers = tiers;
        
        // Update current value pack
        if (state.currentValuePack && state.currentValuePack.id === id) {
          state.currentValuePack.tiers = tiers;
        }
        
        // Update value packs list
        const index = state.valuePacks.findIndex(pack => pack.id === id);
        if (index !== -1) {
          state.valuePacks[index].tiers = tiers;
        }
      })
      .addCase(fetchValuePackTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Save Value Pack Tiers
      .addCase(saveValuePackTiers.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'saveValuePackTiers';
      })
      .addCase(saveValuePackTiers.fulfilled, (state, action) => {
        state.submitting = false;
        state.currentOperation = null;
        
        const { id, tiers } = action.payload;
        
        // Update form data
        state.formData.tiers = tiers;
        
        // Update current value pack
        if (state.currentValuePack && state.currentValuePack.id === id) {
          state.currentValuePack.tiers = tiers;
        }
        
        // Update value packs list
        const index = state.valuePacks.findIndex(pack => pack.id === id);
        if (index !== -1) {
          state.valuePacks[index].tiers = tiers;
        }
      })
      .addCase(saveValuePackTiers.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      
      // Delete a single tier Value Pack Tier
      .addCase(deleteValuePackTier.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'deleteValuePackTier';
      })
      .addCase(deleteValuePackTier.fulfilled, (state, action) => {
        state.submitting = false;
        state.currentOperation = null;
        
        const { valuePackId, tierId } = action.payload;
        
        // Remove tier from form data
        if (state.formData.tiers) {
          state.formData.tiers = state.formData.tiers.filter(t => t.id !== tierId);
        }
        
        // Remove tier from current value pack
        if (state.currentValuePack && state.currentValuePack.id === valuePackId) {
          state.currentValuePack.tiers = state.currentValuePack.tiers.filter(t => t.id !== tierId);
        }
        
        // Remove tier from value packs list
        const index = state.valuePacks.findIndex(pack => pack.id === valuePackId);
        if (index !== -1 && state.valuePacks[index].tiers) {
          state.valuePacks[index].tiers = state.valuePacks[index].tiers.filter(t => t.id !== tierId);
        }
      })
      .addCase(deleteValuePackTier.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })

      // Delete all 3 tiers with a specific duration and specific counrty with specific valuepack
      .addCase(deleteValuePackTiersByCriteria.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'deleteValuePackTier';
      })
      .addCase(deleteValuePackTiersByCriteria.fulfilled, (state, action) => {
        state.submitting = false;
        state.currentOperation = null;
        
        const { valuePackId, duration, countryCode } = action.payload;
        
        // Remove tiers from all relevant state locations
        const filterTiers = (tiers) => tiers.filter(tier => 
          !(tier.duration === duration && tier.countryCode === countryCode)
        );
        
        // Update form data
        if (state.formData.tiers) {
          state.formData.tiers = filterTiers(state.formData.tiers);
        }
        
        // Update current value pack
        if (state.currentValuePack && state.currentValuePack.id === valuePackId) {
          state.currentValuePack.tiers = filterTiers(state.currentValuePack.tiers);
        }
        
        // Update value packs list
        const index = state.valuePacks.findIndex(pack => pack.id === valuePackId);
        if (index !== -1 && state.valuePacks[index].tiers) {
          state.valuePacks[index].tiers = filterTiers(state.valuePacks[index].tiers);
        }
      })
      .addCase(deleteValuePackTiersByCriteria.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })

      .addCase(fetchCommonValuePackData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentOperation = 'fetchCommonValuePackData';
      })
      .addCase(fetchCommonValuePackData.fulfilled, (state, action) => {
        state.loading = false;
        state.commonData = action.payload;
        state.currentOperation = null;
      })
      .addCase(fetchCommonValuePackData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
      .addCase(updateCommonValuePackData.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.currentOperation = 'updateCommonValuePackData';
      })
      .addCase(updateCommonValuePackData.fulfilled, (state, action) => {
        state.submitting = false;
        state.commonData = action.payload;
        state.currentOperation = null;
      })
      .addCase(updateCommonValuePackData.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        state.currentOperation = null;
      })
  }
});

export const {
  setFormData,
  resetFormData,
  updateFormField,
  setCurrentStep,
  nextStep,
  prevStep,
  setFormErrors,
  clearFormErrors,
  clearError,
  setEditMode,
  setCurrentValuePack,
  resetForm,
  addTag,
  removeTag,
  updateValuePackTiersLocal,
  clearFeatures,
  setFeaturesForCategory,
  setFeaturesForCategories,
  setAllCategoriesWithFeatures,
  setPagination,
  setCurrentPage
} = valuePackSlice.actions;

// Selectors
export const selectValuePacks = (state) => state.valuePack.valuePacks;
export const selectCurrentValuePack = (state) => state.valuePack.currentValuePack;
export const selectFormData = (state) => state.valuePack.formData;
export const selectCurrentStep = (state) => state.valuePack.currentStep;
export const selectFormErrors = (state) => state.valuePack.formErrors;
export const selectIsEditMode = (state) => state.valuePack.isEditMode;
export const selectPagination = (state) => state.valuePack.pagination;

// NEW - Optimized selectors
export const selectAllCategoriesWithFeatures = (state) => state.valuePack.allCategoriesWithFeatures;
export const selectFeaturesByCategory = (state) => state.valuePack.featuresByCategory;

// Helper selector to get features for specific categories
export const selectFeaturesForCategories = (state, categoryIds = []) => {
  if (!Array.isArray(categoryIds)) return {};
  
  const result = {};
  categoryIds.forEach(categoryId => {
    const id = typeof categoryId === 'object' ? categoryId.id : categoryId;
    result[id] = state.valuePack.featuresByCategory[id] || { features: [], additionalFeatures: [] };
  });
  return result;
};

// Helper selector to get category with features by ID
export const selectCategoryWithFeatures = (state, categoryId) => {
  const id = typeof categoryId === 'object' ? categoryId.id : categoryId;
  return state.valuePack.allCategoriesWithFeatures.find(cat => cat.id === id) || null;
};

export const selectValuePacksWithCategoryNames = createSelector(
  [selectValuePacks, selectAllCategoriesWithFeatures],
  (valuePacks, allCategories) => {
    if (!Array.isArray(valuePacks) || !Array.isArray(allCategories)) {
      return [];
    }

    return valuePacks
      .filter(valuePack => valuePack !== null && valuePack !== undefined) // Null check add karein
      .map(valuePack => {
        // Additional safety check
        if (!valuePack) return null;
        
        const categoryNames = (valuePack.categories || [])
          .map(cat => {
            // The lean list embeds { id, name }; fall back to the loaded
            // catalog for packs that only carry category ids.
            if (cat && typeof cat === 'object' && cat.name) return cat.name;
            const categoryId = typeof cat === 'object' ? cat.id : cat;
            const category = allCategories.find(c => c.id === categoryId);
            return category ? category.name : null;
          })
          .filter(Boolean);

        return {
          ...valuePack,
          categoryNames
        };
      })
      .filter(pack => pack !== null); // Remove any null packs
  }
);

export const selectLoading = (state) => state.valuePack.loading;
export const selectSubmitting = (state) => state.valuePack.submitting;
export const selectCurrentOperation = (state) => state.valuePack.currentOperation;
export const selectError = (state) => state.valuePack.error;

// Computed selectors
export const selectIsCategoriesLoading = (state) => 
  state.valuePack.loading && ['fetchCategories', 'fetchCategoriesOptimized'].includes(state.valuePack.currentOperation);
export const selectIsValuePacksLoading = (state) => 
  state.valuePack.loading && ['fetchValuePacksOptimized', 'fetchValuePacks'].includes(state.valuePack.currentOperation);
export const selectIsFormLoading = (state) => 
  state.valuePack.loading && ['fetchValuePackById'].includes(state.valuePack.currentOperation);

export const selectCommonData = (state) => state.valuePack.commonData;

export default valuePackSlice.reducer;
